import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, storage, db } from '../firebase';
import { ref, getDownloadURL } from 'firebase/storage';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { Menu } from '@headlessui/react';
import { toast } from 'react-toastify';
import FolderManagerModal from './FolderManagerModal';
import {
  InboxIcon,
  ArchiveBoxIcon,
  FolderIcon,
  DocumentTextIcon,
  XMarkIcon,
  UserCircleIcon,
  ArrowLeftOnRectangleIcon,
  ChevronLeftIcon,
  MagnifyingGlassIcon,
  UserIcon,
  PhotoIcon
} from '@heroicons/react/24/outline';
import PropTypes from 'prop-types';
import logo from './notemail_logo.png';
import { userService } from '../services/UserService';

function Sidebar({ user, mobileMenuOpen, setMobileMenuOpen, isSidebarOpen, setIsSidebarOpen, onOpenSearch }) {
  const { t } = useTranslation();
  const location = useLocation();
  const [profilePicUrl, setProfilePicUrl] = useState(null);
  const [folders, setFolders] = useState([]);
  const [userData, setUserData] = useState(null);
  const [dragOverFolderId, setDragOverFolderId] = useState(null);
  const [isFolderManagerOpen, setIsFolderManagerOpen] = useState(false);

  useEffect(() => {
    if (user) {
      // Set profile picture
      getDownloadURL(ref(storage, `userPictures/${user.uid}.jpg`))
        .then(setProfilePicUrl)
        .catch((error) => {
          // Quietly fail if no profile pic
          console.log('No profile picture found');
        });
    }
  }, [user]);

  // Subscribe to userService for complete user data
  useEffect(() => {
    const updateUserData = (data) => {
      setUserData(data);
    };

    // Initial load
    updateUserData(userService.currentUser);

    // Subscribe to changes
    const unsubscribe = userService.subscribe(updateUserData);

    return unsubscribe;
  }, []);

  // Subscribe to userService for folders
  useEffect(() => {
    const updateFolders = (userData) => {
      if (userData && userData.folders) {
        setFolders(userData.folders);
      } else {
        setFolders([]);
      }
    };

    // Initial load
    updateFolders(userService.currentUser);

    // Subscribe to changes
    const unsubscribe = userService.subscribe(updateFolders);

    return unsubscribe;
  }, []);

  const isActive = (path) => {
    if (path === '/notes' && location.pathname === '/notes') return true;
    return location.pathname === path;
  };
  
  const isActiveFolder = (folderId) => {
    return location.pathname === `/folder/${folderId}`;
  };

  const folderCounts = userData?.folderCounts || { inbox: 0, byFolderId: {} };
  const inboxCount = folderCounts.inbox || 0;
  const getFolderCount = (folderId) => folderCounts.byFolderId?.[folderId] || 0;

  const UserAvatar = () => {
    if (profilePicUrl) {
      return <img src={profilePicUrl} alt="User" className="w-8 h-8 rounded-full object-cover" />;
    } else {
      const displayName = userData?.displayName || user?.displayName;
      const initials = displayName ? displayName.charAt(0).toUpperCase() : '?';
      return (
        <div className="w-8 h-8 rounded-full bg-orange-500 flex items-center justify-center text-white text-sm font-bold">
          {initials}
        </div>
      );
    }
  };

  const navItemClasses = (active, isDragOver) =>
    `group flex items-center px-3 py-2 text-base md:text-sm font-medium rounded-md transition-colors duration-150 ease-in-out ${
      active
        ? 'bg-gray-100 text-orange-600'
        : isDragOver
        ? 'bg-blue-100 text-blue-600 ring-2 ring-blue-400 ring-inset'
        : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
    }`;

  // Desktop: control sidebar visibility with isSidebarOpen prop
  const sidebarTransform = isSidebarOpen ? 'md:translate-x-0' : 'md:-translate-x-full';

  // Drag and Drop Handlers
  const handleDragOver = (e, folderId) => {
    e.preventDefault();
    // Check if the item being dragged is compatible (optional validation)
    setDragOverFolderId(folderId);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Prevent flickering by only resetting if we're leaving the container itself, 
    // not just entering a child element
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverFolderId(null);
    }
  };

  const handleDrop = async (e, folderId) => {
    e.preventDefault();
    setDragOverFolderId(null);
    
    // Try to get noteId from standard or custom data
    const noteId = e.dataTransfer.getData('text/plain') || e.dataTransfer.getData('noteId');
    if (!noteId) return;

    try {
      const noteRef = doc(db, 'notes', noteId);
      await updateDoc(noteRef, {
        folderId: folderId,
        lastModified: serverTimestamp()
      });
      
      const folderName = folderId === 'inbox' ? t('notes.inbox') : 
                         folderId === 'archived' ? t('notes.archive') : 
                         folders.find(f => f.id === folderId)?.name || t('common.folder');
                         
      toast.success(t('sidebar.movedTo', { folder: folderName }));
    } catch (error) {
      console.error('Error moving note:', error);
      toast.error(t('sidebar.failedToMoveNote'));
    }
  };

  return (
    <>
      {/* Mobile Overlay */}
      {mobileMenuOpen && (
        <div 
          className="fixed inset-0 z-40 bg-gray-600 bg-opacity-75 md:hidden"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar Container */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white transform transition-transform duration-300 ease-in-out ${sidebarTransform} flex flex-col border-r border-gray-200 ${
          mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        {/* Header / Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200">
           {/* Logo is always visible in sidebar header */}
           <img src={logo} alt="NoteMail" className="h-8 w-auto" />

           {/* Mobile Close Button */}
          <button
            type="button"
            className="text-gray-500 hover:text-gray-700 focus:outline-none md:hidden"
            onClick={() => setMobileMenuOpen(false)}
          >
            <span className="sr-only">{t('sidebar.closeSidebar')}</span>
            <XMarkIcon className="h-6 w-6" aria-hidden="true" />
          </button>

          {/* Desktop Collapse Button (inside sidebar) */}
          <button
            type="button"
            className="hidden md:flex text-gray-500 hover:text-gray-700 focus:outline-none p-1 rounded-md hover:bg-gray-100"
            onClick={() => setIsSidebarOpen(false)}
            title={t('sidebar.closeSidebar')}
          >
            <span className="sr-only">{t('sidebar.closeSidebar')}</span>
            <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>

        {/* Navigation Links */}
        <div className="flex-1 flex flex-col overflow-y-auto pt-5 pb-4">
          <nav className="px-2 space-y-1">
            {/* Search Button - Progression.io style */}
            <button
              onClick={() => {
                onOpenSearch?.();
                setMobileMenuOpen(false);
              }}
              className="w-full flex items-center justify-between px-3 py-2 mb-3 text-base md:text-sm bg-gray-20 hover:bg-gray-100 border border-gray-110 rounded-lg shadow-sm transition-all duration-150 group"
            >
              <div className="flex items-center gap-2.5">
                <MagnifyingGlassIcon
                  className="h-5 w-5 text-gray-500"
                  aria-hidden="true"
                />
                <span className="text-gray-500 font-normal">{t('sidebar.search')}</span>
              </div>
              <div className="hidden sm:flex items-center gap-1">
                <kbd className="flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded shadow-sm">
                  ⌘
                </kbd>
                <kbd className="flex items-center justify-center min-w-[24px] h-6 px-1.5 text-xs font-medium text-gray-500 bg-white border border-gray-300 rounded shadow-sm">
                  K
                </kbd>
              </div>
            </button>

            {/* Inbox Section */}
            <Link
              to="/inbox"
              className={navItemClasses(
                isActive('/inbox') || location.pathname === '/' || location.pathname === '/notes',
                dragOverFolderId === 'inbox'
              )}
              onClick={() => setMobileMenuOpen(false)}
              onDragOver={(e) => handleDragOver(e, 'inbox')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'inbox')}
            >
              <div className="flex items-center flex-1">
                <InboxIcon
                  className={`mr-3 flex-shrink-0 h-5 w-5 ${
                    isActive('/inbox') || location.pathname === '/' || location.pathname === '/notes' ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                  }`}
                  aria-hidden="true"
                />
                <span>{t('sidebar.inbox')}</span>
              </div>
              {inboxCount > 0 && (
                <span className="bg-red-500 text-white text-sm md:text-xs font-medium px-2 py-0.5 rounded-full ml-auto">
                  {inboxCount}
                </span>
              )}
            </Link>

            <div className="my-4 border-t border-gray-200" />

            {/* All Notes - Not a drop target usually, but could be Inbox */}
            <Link
              to="/all"
              className={navItemClasses(isActive('/all'), false)}
              onClick={() => setMobileMenuOpen(false)}
            >
              <DocumentTextIcon
                className={`mr-3 flex-shrink-0 h-5 w-5 ${
                  isActive('/all') ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
                aria-hidden="true"
              />
              {t('sidebar.allNotes')}
            </Link>

            {/* Archive */}
            <Link
              to="/archive"
              className={navItemClasses(isActive('/archive'), dragOverFolderId === 'archived')}
              onClick={() => setMobileMenuOpen(false)}
              onDragOver={(e) => handleDragOver(e, 'archived')}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, 'archived')}
            >
              <ArchiveBoxIcon
                className={`mr-3 flex-shrink-0 h-5 w-5 ${
                  isActive('/archive') ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
                aria-hidden="true"
              />
              {t('sidebar.archive')}
            </Link>

            {/* Gallery */}
            <Link
              to="/gallery"
              className={navItemClasses(isActive('/gallery'), false)}
              onClick={() => setMobileMenuOpen(false)}
            >
              <PhotoIcon
                className={`mr-3 flex-shrink-0 h-5 w-5 ${
                  isActive('/gallery') ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
                aria-hidden="true"
              />
              {t('sidebar.gallery')}
            </Link>

            <div className="my-4 border-t border-gray-200" />

            {/* Contacts */}
            <Link
              to="/contacts"
              className={navItemClasses(
                isActive('/contacts') || location.pathname.startsWith('/contacts/'),
                false
              )}
              onClick={() => setMobileMenuOpen(false)}
            >
              <UserIcon
                className={`mr-3 flex-shrink-0 h-5 w-5 ${
                  isActive('/contacts') || location.pathname.startsWith('/contacts/') ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                }`}
                aria-hidden="true"
              />
              {t('sidebar.contacts')}
            </Link>
            
            <div className="my-4 border-t border-gray-200" />

            {/* Folders Section */}
            <div className="pt-2">
              <div className="flex items-center justify-between px-3 mb-2">
                <h3 className="text-sm md:text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  {t('sidebar.folders')}
                </h3>
                <button
                  onClick={() => setIsFolderManagerOpen(true)}
                  className="text-sm md:text-xs text-orange-500 hover:text-orange-600 font-medium"
                >
                  {t('common.edit')}
                </button>
              </div>
              
              {folders && folders.length > 0 ? (
                <div className="space-y-1">
                  {folders.map((folder) => (
                    <Link
                      key={folder.id}
                      to={`/folder/${folder.id}`}
                      className={navItemClasses(isActiveFolder(folder.id), dragOverFolderId === folder.id)}
                      onClick={() => setMobileMenuOpen(false)}
                      onDragOver={(e) => handleDragOver(e, folder.id)}
                      onDragLeave={handleDragLeave}
                      onDrop={(e) => handleDrop(e, folder.id)}
                    >
                      <FolderIcon
                        className={`mr-3 flex-shrink-0 h-5 w-5 ${
                          isActiveFolder(folder.id) ? 'text-orange-500' : 'text-gray-400 group-hover:text-gray-500'
                        }`}
                        aria-hidden="true"
                      />
                      <span className="truncate">{folder.name}</span>
                      {getFolderCount(folder.id) > 0 && (
                        <span className="ml-auto text-sm md:text-xs text-gray-500">
                          {getFolderCount(folder.id)}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="px-3 py-2 text-sm md:text-xs text-gray-400 italic">
                  {t('sidebar.noFoldersYet')}
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* User Section (Bottom) */}
        <div className="border-t border-gray-200 p-4">
          <Menu as="div" className="relative">
            <Menu.Button className="group block w-full flex-shrink-0">
              <div className="flex items-center">
                <div className="inline-block h-9 w-9 rounded-full">
                  <UserAvatar />
                </div>
                <div className="ml-3 flex-1 min-w-0 text-left">
                  <p className="text-base md:text-sm font-medium text-gray-700 group-hover:text-gray-900 truncate">
                    {userData?.displayName || user?.displayName || t('sidebar.user')}
                  </p>
                  <p className="text-sm md:text-xs text-gray-500 truncate">
                    {userData?.email || user?.email}
                  </p>
                </div>
              </div>
            </Menu.Button>
            <Menu.Items className="absolute bottom-full left-0 mb-2 w-56 origin-bottom-left divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
              <div className="px-1 py-1">
                <Menu.Item>
                  {({ active }) => (
                    <Link
                      to="/profile"
                      className={`${
                        active ? 'bg-orange-500 text-white' : 'text-gray-900'
                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <UserCircleIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                      {t('sidebar.profile')}
                    </Link>
                  )}
                </Menu.Item>
              </div>
              <div className="px-1 py-1">
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => auth.signOut()}
                      className={`${
                        active ? 'bg-orange-500 text-white' : 'text-gray-900'
                      } group flex w-full items-center rounded-md px-2 py-2 text-sm`}
                    >
                      <ArrowLeftOnRectangleIcon className="mr-2 h-5 w-5" aria-hidden="true" />
                      {t('sidebar.logOut')}
                    </button>
                  )}
                </Menu.Item>
              </div>
            </Menu.Items>
          </Menu>
        </div>
      </div>
      
      <FolderManagerModal 
        isOpen={isFolderManagerOpen} 
        onClose={() => setIsFolderManagerOpen(false)} 
      />
    </>
  );
}

Sidebar.propTypes = {
  user: PropTypes.object.isRequired,
  mobileMenuOpen: PropTypes.bool.isRequired,
  setMobileMenuOpen: PropTypes.func.isRequired,
  isSidebarOpen: PropTypes.bool,
  setIsSidebarOpen: PropTypes.func,
  onOpenSearch: PropTypes.func,
};

export default Sidebar;
